import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@app/environment/environment';
import {
  INVALID_2FA_LOGIN_MESSAGE,
  isInvalid2faLoginHttpError,
} from '@app/helpers/invalid-two-factor-login';
import {
  AcceptExperiencedQualificationRequest,
  Person,
  SetInvestmentStateRequest,
  SetInvestorQualificationRequest,
  UserAccount,
  UserGeneralStatementRequest,
} from '@app/models/account.model';
import { AuthenticationResponse, JwtTokenData } from '@app/models/auth.model';
import { LegalEntityDetails } from '@app/models/project.models';
import { PaginatedData } from '@app/models/shared.models';
import { AccountService } from '@app/services/account.service';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { AuthService } from '@app/services/auth.service';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { jwtDecode } from 'jwt-decode';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

type AuthState = {
  user: UserAccount | null;
  personalData: Person | null;
  accessToken: string | null;
  subAccounts: LegalEntityDetails[] | null;
  isLoading: boolean;
  error: string | null;
  loggedInCompany: LegalEntityDetails | null;
};

const initialState: AuthState = {
  user: null,
  personalData: null,
  accessToken: null,
  subAccounts: null,
  isLoading: false,
  error: null,
  loggedInCompany: null,
};

const ERRORLESS_REDIRECTION = 'Redirecting';
const ACCESS_TOKEN_STORAGE_KEY = 'accessToken';
const REMEMBER_ME_STORAGE_KEY = 'rememberMe';
const TOKEN_EXPIRES_AT_STORAGE_KEY = 'tokenExpiresAt';

type InitSessionState = {
  savedExpiresAt: string | null;
  savedToken: string | null;
  effectiveExpiryMs: number | null;
};

function extractHttpErrorMessage(error: HttpErrorResponse): string {
  const body = error.error;
  if (typeof body === 'string') {
    const t = body.trim();
    if (t) {
      return t;
    }
  }
  if (body && typeof body === 'object') {
    const o = body as { title?: string; message?: string };
    return o.title || o.message || error.message || 'An unknown error occurred';
  }
  return error.message || 'An unknown error occurred';
}

function isLocalhostRuntime(): boolean {
  const hostname = globalThis.location?.hostname ?? '';
  return (
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  );
}

function getTokenExpiryMs(token: string): number | null {
  try {
    const decoded = jwtDecode<JwtTokenData>(token);
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

function parseStoredExpiryMs(value: string | null): number | null {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getRememberMeFromStorage(): boolean {
  return JSON.parse(localStorage.getItem(REMEMBER_ME_STORAGE_KEY) || 'false');
}

function resolveSavedTokenExpiryMs(
  token: string | null,
  storedExpiresAt: string | null,
): number | null {
  const storedExpiryMs = parseStoredExpiryMs(storedExpiresAt);
  if (storedExpiryMs != null) {
    return storedExpiryMs;
  }
  if (!token) {
    return null;
  }
  return getTokenExpiryMs(token);
}

function ensureStoredExpiryFromToken(token: string): void {
  const storedExpiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_STORAGE_KEY);
  if (storedExpiresAt) {
    return;
  }

  const tokenExpiryMs = getTokenExpiryMs(token);
  if (!tokenExpiryMs) {
    return;
  }

  localStorage.setItem(
    TOKEN_EXPIRES_AT_STORAGE_KEY,
    new Date(tokenExpiryMs).toISOString(),
  );
}

function readInitSessionState(): InitSessionState {
  const savedExpiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_STORAGE_KEY);
  const savedToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  return {
    savedExpiresAt,
    savedToken,
    effectiveExpiryMs: resolveSavedTokenExpiryMs(savedToken, savedExpiresAt),
  };
}

function hasSessionHint(session: InitSessionState): boolean {
  return !!session.savedExpiresAt || !!session.savedToken;
}

function clearExpiredSessionArtifacts(
  session: InitSessionState,
): InitSessionState {
  if (
    session.effectiveExpiryMs == null ||
    session.effectiveExpiryMs > Date.now()
  ) {
    return session;
  }

  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_AT_STORAGE_KEY);
  return {
    savedExpiresAt: null,
    savedToken: null,
    effectiveExpiryMs: null,
  };
}

function buildIsoDateFromMs(value: number): string {
  return new Date(value).toISOString();
}

function canUseLocalTokenBootstrap(
  token: string | null,
  effectiveExpiryMs: number | null,
): token is string {
  return (
    isLocalhostRuntime() &&
    !!token &&
    effectiveExpiryMs != null &&
    effectiveExpiryMs > Date.now()
  );
}

/** Module-level timer handle for proactive token refresh. */
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

/** Late-bound scheduler — assigned after `methods` is constructed. */
let scheduleTokenRefresh: (expiresAt: string) => void = (_: string) => {};

export const AuthStore = signalStore(
  {
    providedIn: 'root',
  },
  withState(initialState),
  withComputed(({ user, accessToken, personalData, loggedInCompany }) => {
    const isLoggedIn = computed(() => !!accessToken() && !!user());
    const isGuest = computed(() => !isLoggedIn());
    const isAdmin = computed(() => user()?.roles?.includes('Admin') ?? false);
    const isVerified = computed(() => user()?.verified ?? false);

    const isBasicRegistrationCompleted = computed(() => {
      return (
        isLoggedIn() &&
        (isAdmin() ||
          (user()?.twoFactorEnabled &&
            user()?.termAccepted &&
            user()?.stepUserRepresentsSet))
      );
    });

    const isRegisteredPersonalUser = computed(
      () => isBasicRegistrationCompleted() && user()?.represents === 0,
    );

    const isQualificationCompleted = computed(
      () => isRegisteredPersonalUser() && user()?.stepInvestorQualification,
    );

    const isRegisteredLegalPerson = computed(
      () => isBasicRegistrationCompleted() && user()?.represents === 1,
      //TODO: Add user check based on token if already logged in with the user tpye (legal entity in this case)
    );

    const decodedToken = computed<JwtTokenData | null>(() => {
      const token = accessToken();

      if (!token) {
        return null;
      }

      return jwtDecode(token);
    });

    const loggedInCompanyId = computed(() => {
      return decodedToken()?.companyId ?? null;
    });

    const loggedInCompanyName = computed(() => {
      return decodedToken()?.CompanyName ?? null;
    });

    return {
      isLoggedIn,
      isGuest,
      isAdmin,
      isVerified,
      isBasicRegistrationCompleted,
      isRegisteredPersonalUser,
      isQualificationCompleted,
      isRegisteredLegalPerson,
      currentAccessToken: computed(() => accessToken()),
      currentUser: computed(() => user()),
      decodedToken,
      loggedInCompanyId,
      loggedInCompanyName,
      isUserQualifiedInvestor: computed(() =>
        loggedInCompany() != null
          ? loggedInCompany()?.investorQualification == 1
          : user()?.finishedQualification && user()?.investorQualification == 1,
      ),
    };
  }),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      accountService = inject(AccountService),
      router = inject(Router),
      route = inject(ActivatedRoute),
      legalEntityService = inject(LegalEntityService),
    ) => {
      const saveTokens = (
        tokens: AuthenticationResponse,
        rememberMe: boolean,
      ) => {
        patchState(store, {
          accessToken: tokens.token,
        });
        localStorage.setItem(
          REMEMBER_ME_STORAGE_KEY,
          JSON.stringify(rememberMe),
        );
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, tokens.token);
        if (tokens.expiresAt) {
          localStorage.setItem(TOKEN_EXPIRES_AT_STORAGE_KEY, tokens.expiresAt);
          scheduleTokenRefresh(tokens.expiresAt);
        }
      };

      const clearTokensAndState = () => {
        if (proactiveRefreshTimer !== null) {
          clearTimeout(proactiveRefreshTimer);
          proactiveRefreshTimer = null;
        }
        patchState(store, initialState);
        localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
        localStorage.removeItem(TOKEN_EXPIRES_AT_STORAGE_KEY);
      };

      const loadPersonalData = (): Observable<Person> => {
        return accountService.getPersonalData().pipe(
          tap((data) => {
            patchState(store, { personalData: data });
          }),
          catchError((err) => {
            console.error('Failed to fetch personal data:', err);
            return throwError(() => new Error('Failed to load personal data.'));
          }),
        );
      };

      const fetchUser = (rememberMe: boolean): Observable<UserAccount> => {
        return accountService.getAccountDetails().pipe(
          map((accountDetails) => {
            patchState(store, {
              user: accountDetails,
              isLoading: false,
              error: null,
            });
            return accountDetails;
          }),
          catchError((err) => {
            console.error('Failed to fetch user account details:', err);
            patchState(store, {
              isLoading: false,
              error: 'Failed to load user profile.',
            });
            return throwError(() => new Error('Failed to load user profile.'));
          }),
        );
      };

      const handleError = (
        error: HttpErrorResponse,
        context: string = 'Auth',
      ) => {
        console.error(`${context} Error:`, error);
        const message = extractHttpErrorMessage(error);
        patchState(store, { isLoading: false, error: message });
        return throwError(() => new Error(message));
      };

      const handlePostLoginNavigation = (
        accountDetails: UserAccount | null,
      ) => {
        if (
          !!accountDetails &&
          ((accountDetails?.roles?.includes('Admin') ?? false) ||
            (accountDetails?.twoFactorEnabled &&
              accountDetails?.termAccepted &&
              accountDetails?.stepUserRepresentsSet))
        ) {
          const returnUrl =
            router.routerState.snapshot.root.queryParams['returnUrl'] ??
            '/profile';
          returnUrl && router.navigateByUrl(returnUrl);
        } else {
          router.navigate(['/auth/registration/wizard']);
        }
      };
      const methods = {
        initAuthState(): Observable<UserAccount | null> {
          const rememberMe = getRememberMeFromStorage();
          const hydrateAccessTokenFromSession = (session: InitSessionState) => {
            if (!session.savedToken) {
              return;
            }

            // Immediately hydrate the in-memory access token so requests fired
            // before the silent refresh completes can still attach a Bearer token.
            patchState(store, { accessToken: session.savedToken });
            if (
              !session.savedExpiresAt &&
              session.effectiveExpiryMs != null
            ) {
              localStorage.setItem(
                TOKEN_EXPIRES_AT_STORAGE_KEY,
                buildIsoDateFromMs(session.effectiveExpiryMs),
              );
            }
          };
          const fetchUserForInit = (
            warningMessage: string,
          ): Observable<UserAccount | null> => {
            patchState(store, { isLoading: true });
            return fetchUser(rememberMe).pipe(
              catchError((err) => {
                console.warn(warningMessage, err);
                return of(null);
              }),
            );
          };

          // Use tokenExpiresAt as a hint that a previous session existed.
          // The actual auth is handled by the HttpOnly cookie — if it's gone,
          // refreshTokenInternal will fail gracefully and clear state.
          let session = readInitSessionState();
          const hadExpiredSession =
            session.effectiveExpiryMs != null &&
            session.effectiveExpiryMs <= Date.now();
          session = clearExpiredSessionArtifacts(session);
          if (hadExpiredSession) {
            patchState(store, { accessToken: null });
          }

          if (!hasSessionHint(session)) {
            return of(null);
          }
          hydrateAccessTokenFromSession(session);

          if (
            canUseLocalTokenBootstrap(
              session.savedToken,
              session.effectiveExpiryMs,
            )
          ) {
            ensureStoredExpiryFromToken(session.savedToken);
            if (session.effectiveExpiryMs != null) {
              scheduleTokenRefresh(buildIsoDateFromMs(session.effectiveExpiryMs));
            }
            return fetchUserForInit('Localhost token restore failed');
          }

          patchState(store, { isLoading: true });
          return this.refreshTokenInternal().pipe(
            switchMap(() => fetchUser(rememberMe)),
            catchError((err) => {
              console.warn('Silent refresh on app init failed', err);
              return of(null);
            }),
          );
        },

        login(
          credentials: { email: string; password: string },
          rememberMe: boolean,
        ): Observable<void> {
          patchState(store, { isLoading: true, error: null });
          const payload = { ...credentials, rememberMe };
          return authService.login(payload).pipe(
            tap((response) => saveTokens(response, rememberMe)),
            tap((response) => {
              if (response.requires2Fa) {
                const currentQueryParams = route.snapshot.queryParams;

                const urlTree = router.createUrlTree(['/auth/2fa'], {
                  queryParams: currentQueryParams,
                });
                router.navigateByUrl(urlTree, {
                  state: { credentials: { ...credentials, rememberMe } },
                });
                throw new Error(ERRORLESS_REDIRECTION);
              }
            }),
            switchMap(() => fetchUser(rememberMe)),
            tap((accountDetails) => handlePostLoginNavigation(accountDetails)),
            catchError((err) => {
              if (err.message === ERRORLESS_REDIRECTION) {
                return EMPTY;
              }
              return handleError(err, 'Login');
            }),
            switchMap(() => EMPTY),
          );
        },

        loginWith2fa(
          requestData: {
            email: string;
            password: string;
            code2fa: string;
          },
          rememberMe: boolean,
        ): Observable<void> {
          patchState(store, { isLoading: true, error: null });

          return authService.loginWith2fa(requestData).pipe(
            tap((response) => saveTokens(response, rememberMe)),
            switchMap(() => fetchUser(rememberMe)),
            tap((accountDetails) => handlePostLoginNavigation(accountDetails)),
            catchError((err) => {
              if (
                err instanceof HttpErrorResponse &&
                isInvalid2faLoginHttpError(err)
              ) {
                patchState(store, { isLoading: false, error: null });
                return throwError(() => new Error(INVALID_2FA_LOGIN_MESSAGE));
              }
              return handleError(err, 'Login2FA');
            }),
            switchMap(() => EMPTY),
          );
        },

        loginAsCompany(
          legalEntityId: string,
          rememberMe: boolean,
        ): Observable<void> {
          patchState(store, { isLoading: true, error: null });
          return authService.loginAsCompany({ legalEntityId }).pipe(
            tap((response) =>
              saveTokens(
                response,
                getRememberMeFromStorage(),
              ),
            ),
            switchMap(() => fetchUser(rememberMe)),
            tap((accountDetails) => handlePostLoginNavigation(accountDetails)),
            catchError((err) => {
              if (err.message === ERRORLESS_REDIRECTION) {
                return EMPTY;
              }
              return handleError(err, 'Login');
            }),
            map(() => void 0),
          );
        },

        logoutAsCompany(): Observable<void> {
          patchState(store, { isLoading: true, error: null });
          return authService.logoutFromCompany().pipe(
            tap((response) =>
              saveTokens(
                response,
                getRememberMeFromStorage(),
              ),
            ),
            switchMap(() => fetchUser(getRememberMeFromStorage())),
            tap((accountDetails) => handlePostLoginNavigation(accountDetails)),
            catchError((err) => {
              if (err.message === ERRORLESS_REDIRECTION) {
                return EMPTY;
              }
              return handleError(err, 'Logout from company');
            }),
            map(() => void 0),
          );
        },

        logout(): void {
          patchState(store, { isLoading: true });
          authService.logout().subscribe({
            next: () => {
              clearTokensAndState();
              router.navigate(['/auth/login']);
            },
            error: () => {
              clearTokensAndState();
              router.navigate(['/auth/login']);
            },
          });
        },

        refreshTokenInternal(): Observable<AuthenticationResponse> {
          patchState(store, { isLoading: true });
          return authService.refreshToken().pipe(
            tap((tokens) => {
              // Keep isLoading: true — caller (e.g. initAuthState) will still
              // pipe into fetchUser. isLoading is cleared there or on error.
              patchState(store, { accessToken: tokens.token });
              localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, tokens.token);
              if (tokens.expiresAt) {
                localStorage.setItem(
                  TOKEN_EXPIRES_AT_STORAGE_KEY,
                  tokens.expiresAt,
                );
                scheduleTokenRefresh(tokens.expiresAt);
              }
            }),
            catchError((err) => {
              console.error('Refresh token failed:', err);
              clearTokensAndState();
              patchState(store, {
                isLoading: false,
                error: 'Session expired.',
              });
              return throwError(
                () => new Error('Session expired. Please log in again.'),
              );
            }),
          );
        },

        setGeneralTermsAndConditions(
          payload: UserGeneralStatementRequest,
        ): Observable<UserAccount | null> {
          return accountService.setGeneralTermsAndConditions(payload).pipe(
            tap((accountDetails) => {
              patchState(store, { user: accountDetails });
            }),
            catchError((err) => {
              console.error('Failed to set general terms and conditions:', err);
              return throwError(
                () => new Error('Failed to set general terms and conditions.'),
              );
            }),
          );
        },

        setInvestorQualification(
          payload: SetInvestorQualificationRequest,
        ): Observable<UserAccount | null> {
          return accountService.setInvestorQualification(payload).pipe(
            tap((accountDetails) => {
              patchState(store, { user: accountDetails });
            }),
            catchError((err) => {
              console.error('Failed to set investor qualification:', err);
              return throwError(
                () => new Error('Failed to set investor qualification.'),
              );
            }),
          );
        },

        setInvestorQualificationForLegalEntity(
          legalEntityId: string,
          payload: SetInvestorQualificationRequest,
        ): Observable<LegalEntityDetails | null> {
          return accountService
            .setInvestorQualificationForLegalEntity(legalEntityId, payload)
            .pipe(
              tap((accountDetails) => {
                patchState(store, { loggedInCompany: accountDetails });
              }),
              catchError((err) => {
                console.error('Failed to set investor qualification:', err);
                return throwError(
                  () => new Error('Failed to set investor qualification.'),
                );
              }),
            );
        },

        setStartOfKnowledgeTest(): Observable<UserAccount | null> {
          return accountService.setStartOfKnowledgeTest().pipe(
            tap((accountDetails) => {
              patchState(store, { user: accountDetails });
            }),
            catchError((err) => {
              console.error('Failed to set start of knowledge test:', err);
              return throwError(
                () => new Error('Failed to set start of knowledge test.'),
              );
            }),
          );
        },

        acceptExperiencedQualification(
          payload: AcceptExperiencedQualificationRequest,
        ): Observable<UserAccount | null> {
          return accountService.acceptExperiencedQualification(payload).pipe(
            tap((accountDetails) => {
              patchState(store, { user: accountDetails });
            }),
            catchError((err) => {
              console.error('Failed to accept experienced qualification:', err);
              return throwError(
                () => new Error('Failed to accept experienced qualification.'),
              );
            }),
          );
        },

        setInvestorStatement(
          payload: SetInvestmentStateRequest,
        ): Observable<UserAccount | null> {
          return accountService.setInvestorStatement(payload).pipe(
            tap((accountDetails) => {
              patchState(store, { user: accountDetails });
            }),
            catchError((err) => {
              console.error('Failed to set investor statement:', err);
              return throwError(
                () => new Error('Failed to set investor statement.'),
              );
            }),
          );
        },

        refreshUserProfile(): Observable<UserAccount | null> {
          patchState(store, { isLoading: true });
          const rememberMe = getRememberMeFromStorage();
          return fetchUser(rememberMe).pipe(
            catchError((err) => {
              patchState(store, { isLoading: false });
              return of(null);
            }),
          );
        },
        loadPersonalData,

        loadSubAccounts(): Observable<PaginatedData<LegalEntityDetails>> {
          return accountService.getSubAccounts().pipe(
            tap((response) => {
              patchState(store, { subAccounts: response.data });
            }),
          );
        },

        handle401Error(): Observable<string> {
          return this.refreshTokenInternal().pipe(
            tap(() => patchState(store, { isLoading: false })),
            map((tokens) => tokens.token),
            catchError((error) => {
              router.navigate(['/auth/login'], {
                queryParams: { returnUrl: router.url },
              });
              return throwError(() => error);
            }),
          );
        },

        savePersonalData(request: Person): Observable<Person> {
          return accountService.setPersonalData(request).pipe(
            tap((response) => {
              const user = store.user();

              user &&
                patchState(store, {
                  user: {
                    ...user,
                    personalData: response,
                  },
                });
            }),
          );
        },

        refreshLoggedInCompany(): Observable<LegalEntityDetails> {
          const id = store.loggedInCompanyId();
          if (!id) {
            return throwError(
              () => new Error('No logged-in company to refresh.'),
            );
          }
          return legalEntityService.getLegalEntityById(id).pipe(
            tap((response: LegalEntityDetails) => {
              patchState(store, { loggedInCompany: response });
            }),
          );
        },
      };

      scheduleTokenRefresh = (expiresAt: string) => {
        if (proactiveRefreshTimer !== null) {
          clearTimeout(proactiveRefreshTimer);
          proactiveRefreshTimer = null;
        }
        const jitterMs = 3_000 + Math.random() * 7_000; // 3–10s random jitter
        const refreshInMs = new Date(expiresAt).getTime() - Date.now() - environment.tokenRefreshBufferMs - jitterMs;
        const doRefresh = () => {
          // If another request is in flight, retry shortly instead of silently giving up
          if (store.isLoading()) {
            proactiveRefreshTimer = setTimeout(doRefresh, 5_000);
            return;
          }
          methods.refreshTokenInternal().subscribe({
            next: () => patchState(store, { isLoading: false }),
            error: () => methods.logout(),
          });
        };
        if (refreshInMs <= 0) {
          doRefresh();
          return;
        }
        proactiveRefreshTimer = setTimeout(doRefresh, refreshInMs);
      };

      return methods;
    },
  ),
  withHooks({
    onInit: (store) => {
      const legalEntityService = inject(LegalEntityService);

      console.log('Auth Store initialized');
      !environment.production &&
        effect(() => store.error() && console.log(store.error()));

      effect(() => {
        const loggedInCompanyId = store.decodedToken()?.companyId;
        if (
          loggedInCompanyId &&
          loggedInCompanyId != store.loggedInCompany()?.id
        ) {
          legalEntityService
            .getLegalEntityById(loggedInCompanyId)
            .subscribe((response) => {
              patchState(store, {
                loggedInCompany: response,
              });
            });
        }
        if (loggedInCompanyId == undefined) {
          patchState(store, {
            loggedInCompany: undefined,
          });
        }
      });
    },
  }),
);
